import { Box } from '@zillow/constellation-10';
import { isString } from 'src/utils/typeguards/isString';
import { css } from '@/styled-system/css';
import { FunctionComponentWithChildren } from 'src/utils/types';
import { BaseSkeleton } from './BaseSkeleton';

export type SkeletonCardProps = {
    /**
     * The height of the card
     */
    height?: string | Record<string, string>;
    /**
     * The width of the card
     */
    width?: string | Record<string, string>;
    /**
     * Whether to render as a block or not.
     */
    block?: boolean;
    /**
     * By default the card renders vertically with default dimensions. You can rotate it to
     * render horizontally without providing custom heigh and width by using this prop
     */
    horizontal?: boolean;
};

const HEIGHTS = {
    HORIZONTAL: '200px',
    VERTICAL: '300px',
} as const;

export const BREAKPOINT_INCLUSION_FLAGS = {
    GTE: '_gte',
} as const;

const handlePolymorphicDimension = (dimensionName: string, dimension: string | Record<string, string>) => {
    if (isString(dimension)) {
        return { [dimensionName]: dimension };
    }

    return Object.entries(dimension).reduce((acc, [breakpoint, value]) => {
        const mediaKey = breakpoint.includes(BREAKPOINT_INCLUSION_FLAGS.GTE)
            ? `@media screen and (min-width: ${breakpoint.replace(BREAKPOINT_INCLUSION_FLAGS.GTE, '')})`
            : `@media screen and (max-width: ${breakpoint})`;

        return {
            ...acc,
            [mediaKey]: { [dimensionName]: value },
        };
    }, {});
};

export const SkeletonCard: FunctionComponentWithChildren<SkeletonCardProps> = ({
    height,
    width,
    block,
    horizontal,
    children,
}) => (
    <BaseSkeleton>
        <Box
            className={css({
                display: block ? 'block' : 'inline-block',
                height: horizontal ? HEIGHTS.HORIZONTAL : HEIGHTS.VERTICAL,
                width: !width ? (horizontal ? '100%' : '300px') : undefined,
                borderRadius: 'radii.lg',
                ...handlePolymorphicDimension('height', height || ''),
                ...(width && handlePolymorphicDimension('width', width)),
            })}>
            {children}
        </Box>
    </BaseSkeleton>
);
